import { base64Encode } from '../helpers/string';

type Username = string;

type Password = string;

type OptionalCredentials = [Username | undefined, Password | undefined];

type FetchHeaders = Record<string, string>;

type FetchHeadersWithCredentials = FetchHeaders & {
  Authorization: string;
};

/**
 * Returns the given headers object with added basic auth credentials.
 * If no credentials are provided, the headers object is returned as-is.
 * If the headers object is not provided, an empty headers object is created.
 *
 * Usage:
 *
 * ```ts
 * const resp = await fetch('/api/example', {
 *  headers: headersWithBasicAuth([
 *    process.env.SECRET_BASIC_AUTH_USER,
 *    process.env.SECRET_BASIC_AUTH_PASSWORD,
 *  ]),
 * });
 * ```
 */
export const headersWithBasicAuth = (
  credentials: OptionalCredentials,
  headers?: FetchHeaders,
): FetchHeaders | FetchHeadersWithCredentials => {
  if (!headers) {
    // Create an empty headers object if none was provided.
    headers = {};
  }

  const [user, password] = credentials;

  // If no credentials are defined, return the (empty) headers object.
  if (!user || !password) {
    return headers;
  }

  // Build up the credentials string.
  const encodedCredentials = base64Encode(`${user}:${password}`);

  // Add the authorization header with the credentials and return the headers object.
  return {
    ...headers,
    Authorization: `Basic ${encodedCredentials}`,
  };
};
